import React, { useState, useRef, useEffect } from "react";
import { focusNextField } from "../../Utils/family-tree/addMemberFieldLimits";

const normalizeOptions = (options) =>
    options.map((opt) =>
        typeof opt === "string" ? { value: opt, label: opt } : opt
    );

export default function DropdownSelect({
    options = [],
    value = "",
    onChange,
    placeholder = "Select",
    maxMenuHeight = 180,
    className = "",
    disabled = false,
    required = false,
    searchable = true, // New prop to enable/disable search
    focusNextOnSelect = false,
    maxSearchLength,
}) {
    const [open, setOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const ref = useRef(null);
    const inputRef = useRef(null);
    const prevOpenRef = useRef(false);
    const opts = normalizeOptions(options);
    const selectedOption = opts.find((o) => String(o.value) === String(value));
    const selected = selectedOption?.label ?? "";

    // String used for search/filter and for input value (label can be React node)
    const getSearchString = (opt) => {
        if (opt.searchLabel != null && opt.searchLabel !== "") return String(opt.searchLabel);
        if (typeof opt.label === "string") return opt.label;
        return "";
    };

    // Filter options based on search term (use searchLabel or string label only)
    const filteredOptions = searchable && searchTerm
        ? opts.filter((opt) =>
            getSearchString(opt).toLowerCase().includes(searchTerm.toLowerCase())
        )
        : opts;

    const selectedSearchString = selectedOption ? getSearchString(selectedOption) : "";

    // Initialize search term with selected value when opening, reset when closing
    useEffect(() => {
        const wasOpen = prevOpenRef.current;
        prevOpenRef.current = open;
        
        if (open && searchable && !wasOpen) {
            // Just opened: initialize searchTerm with string version of selected (safe for input)
            setSearchTerm(selectedSearchString);
        } else if (!open && wasOpen) {
            // Just closed: reset search term
            setSearchTerm("");
        }
    }, [open, searchable, selectedSearchString]);

    // Focus input when dropdown opens and searchable
    useEffect(() => {
        if (open && searchable && inputRef.current) {
            // Small delay to ensure the dropdown is rendered
            setTimeout(() => {
                inputRef.current?.focus();
                // Select all text so user can immediately start typing to replace
                inputRef.current?.select();
            }, 10);
        }
    }, [open, searchable]);

    useEffect(() => {
        const onDocClick = (e) => {
            if (!ref.current) return;
            if (!ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, []);

    const handleSelect = (selectedValue) => {
        onChange?.(selectedValue);
        setOpen(false);
        setSearchTerm("");
        if (focusNextOnSelect) {
            requestAnimationFrame(() => {
                focusNextField(ref.current);
            });
        }
    };

    const handleInputChange = (e) => {
        let newValue = e.target.value;
        if (maxSearchLength != null) {
            newValue = newValue.slice(0, maxSearchLength);
        }
        setSearchTerm(newValue);
        // Open dropdown if not already open
        if (!open) {
            setOpen(true);
        }
    };

    const handleInputKeyDown = (e) => {
        if (disabled) return;
        
        if (e.key === "Escape") {
            setOpen(false);
            setSearchTerm("");
            return;
        }
        
        if (e.key === "Enter") {
            const firstSelectable = filteredOptions.find((opt) => !opt.disabled);
            if (firstSelectable) {
                e.preventDefault();
                handleSelect(firstSelectable.value);
            }
            return;
        }
        
        if (e.key === "ArrowDown") {
            e.preventDefault();
            if (!open) {
                setOpen(true);
            }
            return;
        }
    };
    
    const handleButtonKeyDown = (e) => {
        if (disabled) return;
        
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((v) => !v);
            return;
        }
        
        if (e.key === "Escape") {
            setOpen(false);
            return;
        }
        
        // If searchable, allow typing to open and search
        if (searchable && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
            e.preventDefault();
            setSearchTerm(e.key);
            setOpen(true);
        }
    };

    // Display logic: 
    // - When open and searchable: always show searchTerm (even if empty, so user can clear and type)
    // - When closed: show selected value
    const displayValue = open && searchable 
        ? searchTerm 
        : (selected || "");

    return (
        <div className={`dropdown-selection-cs ${className}`} ref={ref}>
            {open && searchable ? (
                <input
                    ref={inputRef}
                    type="text"
                    className="modal-dropdown-cs"
                    value={displayValue}
                    onChange={handleInputChange}
                    onKeyDown={handleInputKeyDown}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (!open) setOpen(true);
                    }}
                    placeholder=""
                    disabled={disabled}
                    style={{
                        width: "100%",
                        cursor: disabled ? "not-allowed" : "text",
                        backgroundColor: disabled ? "#f5f5f5" : "white",
                        opacity: disabled ? 0.6 : 1,
                    }}
                />
            ) : (
                // <div
                //     className="modal-dropdown-cs"
                //     role="button"
                //     aria-haspopup="listbox"
                //     aria-expanded={open}
                //     tabIndex={0}
                //     onClick={() => !disabled && setOpen((v) => !v)}
                //     onKeyDown={handleButtonKeyDown}
                // >
                //     {selected || placeholder}
                // </div>
                    <div
                        className="modal-dropdown-cs"
                        role="button"
                        aria-haspopup="listbox"
                        aria-expanded={open}
                        tabIndex={disabled ? -1 : 0}
                        onClick={(e) => {
                            if (!disabled) {
                                if (selected) {
                                    // When value is selected, clicking anywhere clears it and opens dropdown
                                    onChange?.("");
                                    setSearchTerm("");
                                    setOpen(true);
                                } else {
                                    // If no value, toggle dropdown
                                    setOpen((v) => !v);
                                }
                            }
                        }}
                        onKeyDown={handleButtonKeyDown}
                        style={{
                            backgroundColor: disabled ? "#f5f5f5" : "white",
                            opacity: disabled ? 0.6 : 1,
                            cursor: disabled ? "not-allowed" : "pointer",
                        }}
                    >
                        {selected || placeholder}
                    </div>

            )}

            {open && !disabled && (
                <div
                    className="dropdown-cs"
                    role="listbox"
                    style={{ 
                        maxHeight: maxMenuHeight, 
                        overflow: "auto",
                        width: "100%",
                        minWidth: "100%",
                        maxWidth: "100%",
                    }}
                >
                    <ul
                        style={{
                            margin: 0,
                            padding: 0,
                            listStyle: "none",
                        }}
                    >
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((o) =>
                                o.disabled ? (
                                    <li
                                        key={o.value}
                                        role="separator"
                                        aria-disabled="true"
                                        className="dropdown-cs-separator"
                                        style={{
                                            padding: "8px 12px",
                                            color: "#999",
                                            fontSize: "12px",
                                            textAlign: "center",
                                            cursor: "default",
                                            userSelect: "none",
                                            pointerEvents: "none",
                                            borderTop: "1px solid #e6e6e6",
                                            borderBottom: "1px solid #e6e6e6",
                                        }}
                                    >
                                        {o.label}
                                    </li>
                                ) : (
                                    <li
                                        key={o.value}
                                        role="option"
                                        aria-selected={String(value) === String(o.value)}
                                        className={String(value) === String(o.value) ? "active" : undefined}
                                        onClick={() => handleSelect(o.value)}
                                        tabIndex={0}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                handleSelect(o.value);
                                            }
                                        }}
                                    >
                                        {o.label}
                                    </li>
                                )
                            )
                        ) : (
                            <li
                                style={{
                                    padding: "12px",
                                    textAlign: "center",
                                    color: "#999",
                                    fontStyle: "italic",
                                    borderBottom: "1px solid #d9d9d9",
                                }}
                            >
                                No options found
                            </li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
}